import { addBreadcrumb, captureException } from '../../sentry-config'
import { Recipients } from '../../types/loft'
import { Item } from '../ews'
import { getAccessToken } from '../nested-app-auth/authHelper'
import { mapRecipient, getGraphUrlMessages } from './utils'

let accessToken: string

export const setAuthTokenGraphApi = async (token: string) => {
  accessToken = token
}

const getCurrentAccessToken = async () => {
  if (!accessToken) {
    return await getAccessToken([
      'user.read',
      'mail.read',
      'mail.readwrite',
      'mail.send',
      'offline_access',
    ])
  }
  return accessToken
}

async function getMessageDetails(messageId: string) {
  const url = `${getGraphUrlMessages()}/${messageId.replaceAll(
    '/',
    '-'
  )}?$select=subject,toRecipients,ccRecipients,internetMessageId,hasAttachments`

  const token = await getCurrentAccessToken()
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    })

    const responseData = await response.json()

    if (!response.ok) {
      throw new Error(`Failed to get message details: ${responseData.error.message}`)
    }

    return responseData
  } catch (error) {
    addBreadcrumb({
      category: 'graphApi',
      message: 'Loft: Failed to get message details',
      level: 'error',
    })
    captureException(error)
    throw error
  }
}

export async function waitSyncAndGetItemById(
  id: string,
  attemptsLeft = 5,
  retryDelay = 3000
): Promise<null | Item> {
  try {
    addBreadcrumb({ message: 'GraphApi: GetItem', category: 'graphApi' })

    return await getMessageDetails(id)
  } catch (error) {
    addBreadcrumb({
      message: `GraphApi: GetItem failed. Attempt left: ${attemptsLeft}`,
      level: 'error',
      category: 'graphApi',
    })
    captureException(error)
    if (attemptsLeft <= 1) {
      throw error
    }
    await new Promise(resolve => setTimeout(resolve, retryDelay))
    return waitSyncAndGetItemById(id, attemptsLeft - 1, retryDelay)
  }
}

export async function updateAndSendItem(
  id: string,
  body: string,
  subject: string,
  recipients: Recipients
) {
  addBreadcrumb({ message: 'Graph API: Sending item', category: 'graphApi' })

  const newBody = `<!DOCTYPE html><html><head></head><body>${body}</body></html>`

  const updatePayload = {
    subject,
    body: {
      content: newBody,
      contentType: 'HTML',
    },
    toRecipients: recipients.to.map(mapRecipient),
    ccRecipients: recipients.cc.map(mapRecipient),
    bccRecipients: recipients.bcc.map(mapRecipient),
  }

  try {
    const token = await getCurrentAccessToken()

    const updateResponse = await fetch(`${getGraphUrlMessages()}/${id.replaceAll('/', '-')}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updatePayload),
    })

    if (!updateResponse.ok) {
      const errorData = await updateResponse.json()
      throw new Error(`Update failed: ${errorData.error.message}`)
    }

    const sendResponse = await fetch(`${getGraphUrlMessages()}/${id.replaceAll('/', '-')}/send`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    if (!sendResponse.ok) {
      const errorData = await sendResponse.json()
      captureException(new Error(`Send failed: ${errorData.error.message}`))
      throw new Error(`Send failed: ${errorData.error.message}`)
    }

    return { updated: true, messageId: id }
  } catch (error) {
    captureException(error)
    throw error
  }
}
